import { BCMSRecord, useBCMSContext, useModel, useStateManager, useWorkflow } from "@bcm-dev/bcms-vite";
import MakeTransfer from "components/workflows/MakeTransferWorkflow/classes/MakeTransfer";
import MakeTransferUI from "components/workflows/MakeTransferWorkflow/classes/MakeTransferUI";
import Classes, { TransferTemplate, TransferTransaction } from "model";
import React from "react";

import { TransferDetails } from "components/pages/TransferReviewPage";
import { CenterContentContainer, ContentHeaderView, FormContainer, PageContainer, PageContentView } from "components/shared";
import { FormButtonRow } from "components/shared/containers/FormContainer";
import { CheckmarkBanner } from "components/shared/views/ConfirmBannerView";
import { UIButton } from "components/ui";
import { getDefaultTransfer } from "components/workflows/MakeTransferWorkflow";

import CustomContentHeaderWidget from "components/shared/views/CustomContentHeaderWidget";
import "./styles.css";

/**
 * @name TransferConfirmPage
 * @param {OBJECT} data - Data passed to the page
 * @param {OBJECT} ui - Ui object to set values of the page ui
 * @returns {React.Component}
 * @description The main component for the TransferConfirmPage
 */
export default function TransferConfirmPage(props) {
    // Initalize all model classes
    const globalController = useBCMSContext();
    const model = useModel();
    BCMSRecord.globalControllerFor(Classes, globalController);
    // Sets the values for the data and ui variables
    const data = { ...getDefaultPageData(), ...props.data ?? {} };
    const ui = { ...getDefaultPageUI(data.transfer), ...props.ui ?? {} };
    const className = props.className?(" "+props.className):"";
    const { goStep } = useWorkflow() ?? {};
    // The state to be passed to the state manager
    const state = { data, ui };

    // :: React.state, React.setState -> OBJECT
    // Function to initialze the controller for the state manager
    const controller = (state, setState) => ({
        data: state.data, // Set the data property in the controller
        ui: state.ui, // Set the ui property in the controller

        // anotherTransfer :: Event -> VOID
        // Go to the first step of the workflow passing the default data of the workflow
        // to initalize a new transfer
        anotherTransfer: evt => goStep(0, getDefaultTransfer(globalController, model))
    });

    return (
        <PageContainer className={"transfer-confirm-page"+className} state={state} controller={controller}>
            <TransferConfirmContent />
        </PageContainer>
    );
}

/**
 * @name getDefaultPageData
 * @returns {OBJECT}
 * @description Hook to return the default data required by the page
 */
export const getDefaultPageData = (transferID) => {
    const { branding, user } = GetModel();

    // Note: user settings determine how transfers are made, 
    // so using templates, using only templates, or no templates at all.
    const allowTransferOnlyTemplates = user.allowTransferOnlyTemplates === "true";
    let editTransfer = undefined;
    let editMakeTransfer = undefined;

    if(typeof transferID === "string") {
        editTransfer = TransferTransaction.initByID(transferID);
        editMakeTransfer = MakeTransfer.init(editTransfer, branding)
        editMakeTransfer.templateOnly = allowTransferOnlyTemplates;
    }

    const transfer = editMakeTransfer ? editMakeTransfer : MakeTransfer.init({ templateOnly: allowTransferOnlyTemplates }, branding);

    return {
        transfer: transfer,
        transferTemplates: TransferTemplate.initAll(),
        editingTransfer: { isEditing: transferID ? "true" : "false" },
        editingTransferID: { id: transferID }
    }
};

function GetModel() {
    return useModel();
}

/**
 * @name getDefaultPageUI
 * @param {MakeTransfer} transfer - A MakeTransfer instance used to initalize an instance of MakeTransferUI
 * @returns {OBJECT}
 * @description Hook to return the default ui required by the page
 * @note The transfer paramter is not required, if nothing is passed than an empty instance of MakeTransfer will be used
 */
export const getDefaultPageUI = transfer => ({
    control: MakeTransferUI.init(transfer ?? MakeTransfer.init()),
    confirmationBanner: { isActive: true }
});

/**
 * @name TransferConfirmContent
 * @returns {React.Component}
 * @description The component that contains all the content shown for the TransferConfirmPage
 */
function TransferConfirmContent(props) {
    const { anotherTransfer, data, ui } = useStateManager();
    const scheduledTransfer = data.transfer.schedule.toLowerCase() === "recurring"
        || data.transfer.schedule === "Future (One Time)";
    const { branding } = useModel();
    const promoText = branding.options.transfers_promo_abi22;

    return (
        <PageContentView>
            <div className="flex-container flex-vertical page-widget-wrapper">
                {promoText && <CustomContentHeaderWidget content={promoText} />}
                <CenterContentContainer>
                    <ContentHeaderView text="Transfer Confirmation" />
                    {ui.confirmationBanner.isActive && !scheduledTransfer &&
                        <CheckmarkBanner text="Your transfer has been successfully submitted. Confirmation number: 22" dataPoint="Checkmark_Banner_Close_Button" />
                    }
                    {ui.confirmationBanner.isActive && scheduledTransfer &&
                        <CheckmarkBanner text="Your transfer has been successfully scheduled." dataPoint="Checkmark_Banner_Close_Button" />
                    }
                    <FormContainer className="small-form-width" >
                        <TransferDetails />

                        <FormButtonRow>
                            <UIButton onClick={anotherTransfer} className="primary-button" text="Make Another Transfer" data-point="Transfers_Make_Another" />
                        </FormButtonRow>
                    </FormContainer>
                </CenterContentContainer>
            </div>
        </PageContentView>
    );
}